import * as React from 'react';
import { Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { ChevronRight } from 'lucide-react';
import {
  HStack,
  Text,
} from '@ayble-health/dashboard-components';
import { Chip } from 'components/ui/chip';
import { StatusPill } from '../ProviderHome/StatusPill';
import { RosterPatient } from '../../models/user';
import { VisitTiming } from './constants';
import { displayedAppointment, isCancelled, isRecentSignup } from './filters';
import { ENGAGED_DATE, parseEngagedAt } from './engagement';
import { ROW_GRID, ROW_PADDING_X } from './columns';

/**
 * Fills its virtualized slot exactly — `height: 100%` against the row height
 * the list reserves, with the row's own box filling this in turn.
 *
 * It used to be content-sized inside a taller slot, leaving 8px of dead space
 * below every row. Invisible at rest, but the hover fill stopped at the box
 * and that 8px stayed white, so hovering any row read as a gap opening up
 * beneath it. No border radius for the same reason: rows now sit flush and a
 * rounded fill would notch the separator line at each corner.
 */
const Hoverable = styled('div')`
  height: 100%;
  transition: background-color 120ms ease-in;
  cursor: pointer;

  &:hover {
    background-color: var(--feather, #eef4f6);
  }
`;

const MEETING_DATE = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});
const MEETING_TIME = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

/**
 * The virtualized slot height, and the row's actual height — the two are the
 * same number on purpose. The row used to be 8px shorter than its slot, which
 * is where the hover gap came from.
 */
export const ROW_HEIGHT = 65;

/**
 * Sized to match `StatusPill`'s `sm` — the same numbers as its `SIZES.sm`
 * entry (px 2 / py 1px / 2xs), deliberately rather than coincidentally.
 *
 * `Chip`'s own base sizing is a step larger, which would make the enrollment
 * badge outweigh the Confirmed/Unconfirmed pill sitting one column over.
 * They're peers — both are a short status marker attached to a row — so they
 * should carry the same visual weight. Prop overrides rather than a new `Chip`
 * size variant: one caller needs this, and adding a variant would put a
 * roster-specific size into a shared primitive.
 *
 * The last four exist because this badge used to be a `Tag`, and Chip's base
 * is not Tag's. They restore the previous rendering exactly rather than
 * introducing anything new:
 *
 * - `minH` — Chakra's Tag slot recipe, which Chip builds on, floors the root
 *   at 20px. Tag had no floor, so `py: '1px'` actually decided the height.
 *   Left in place the badge renders 20px against StatusPill's 16.5px, which is
 *   the size mismatch this whole block exists to prevent.
 * - `lineHeight` — without it the label slot's own 16px decides the height and
 *   `fontSize` buys nothing. `inherit` is what matches the peer: the package
 *   `Text` inside `StatusPill` asks for `lineHeight="base"`, which is not a
 *   token in this theme and is therefore dropped as invalid CSS, leaving it on
 *   the inherited line-height. Inheriting deliberately lands on the same value
 *   without hardcoding the ratio. Both now resolve to a 15px line-height, so
 *   the badge and the outlined `Unconfirmed` pill are both 19px; the filled
 *   `Confirmed` pill is 17px only because it carries no border. Border
 *   presence is a variant choice, and it is now the only thing separating
 *   them.
 * - `letterSpacing` — carried over from the `Tag` this replaced, and shared
 *   with the rest of the careOS pill vocabulary.
 *
 * `fontSize` and `lineHeight` only reach the text because `ui/chip.tsx`
 * forwards typography to Chip's label slot; on the bare package Chip they style
 * the root box and leave the label at 12px. See the note there.
 *
 * The `Tag` this replaced set `lineHeight: 'sm'` (careOS 1.25em), which made it
 * 16.5px — half a pixel shorter than the pill it sits beside. That was never
 * intentional; it was Tag's base leaking into a badge that was explicitly
 * written to match `StatusPill`. Matching the peer is the whole point of this
 * block, so the badge follows the peer rather than preserving Tag's number.
 *
 * `cursor` is separate below — it is behaviour, not size.
 */
const BADGE_SIZE = {
  px: 2,
  py: '1px',
  fontSize: '2xs',
  minH: 0,
  lineHeight: 'inherit',
  letterSpacing: '0.01em',
} as const;

/**
 * The whole row is a click target, so the badge must not interrupt the row's
 * pointer cursor. Chip's base sets `cursor: default` — correct for a chip
 * sitting on its own, wrong for one embedded in a clickable row. `inherit`
 * restores what Tag did by not setting `cursor` at all.
 */
const BADGE_CURSOR = { cursor: 'inherit' } as const;

/**
 * Enrollment badge, on the member's name line.
 *
 * Surfaces the Enrollment Status axis in the row (round 7): the two states
 * round 6 pulled out of the care funnel are the two that change how you read
 * everything else about a member, and neither was visible in the table.
 *
 * Both predicates come from `filters.ts` rather than being re-derived here, so
 * the badge and the Enrollment Status filter cannot disagree about who is New
 * or Cancelled — the table would otherwise be able to contradict the filter
 * that produced it.
 *
 * They can both be true at once: a member can sign up and cancel inside the
 * same week. Cancelled wins, being the more consequential of the two, and
 * because "new" stops being the useful fact about someone who has already
 * left. One badge rather than two — the name cell has one line to spare.
 */
const EnrollmentBadge = ({ user }: { user: RosterPatient }) => {
  if (isCancelled(user)) {
    return (
      <Chip variant="ballet" aria-label="Cancelled" {...BADGE_SIZE} {...BADGE_CURSOR}>
        Cancelled
      </Chip>
    );
  }

  if (isRecentSignup(user, new Date())) {
    return (
      <Chip variant="mint" aria-label="New member" {...BADGE_SIZE} {...BADGE_CURSOR}>
        New
      </Chip>
    );
  }

  return null;
};

export const HealthCoachDashboardListItem = ({
  user,
  appliedVisitTiming = null,
}: {
  user: RosterPatient;
  /**
   * The *applied* appointment-timing filter — never the draft one. It decides
   * whether a past appointment is shown at all, so it has to match what
   * actually filtered this list; reacting to an unapplied sidebar click would
   * change the table out from under the filter that produced it.
   */
  appliedVisitTiming?: VisitTiming | null;
}) => {
  const navigate = useNavigate();
  // Past appointments are hidden unless a past window is explicitly selected,
  // so this is `null` for a member whose only appointment has already happened
  // — and the cell reads "None scheduled", which is what they are. See
  // `displayedAppointment`.
  const meeting = displayedAppointment(user, appliedVisitTiming, new Date());
  const engagedOn = parseEngagedAt(user.last_app_engagement);

  return (
    <Hoverable
      role="button"
      tabIndex={0}
      // `from` tells the profile (and the side nav) which tab this patient was
      // opened from, so Back returns here instead of the provider Home.
      onClick={() => navigate(`/provider-home/patient/${user.id}?from=hc-dashboard`)}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/provider-home/patient/${user.id}?from=hc-dashboard`);
        }
      }}
    >
      <Box
        display="grid"
        gridTemplateColumns={ROW_GRID}
        alignItems="center"
        gap={4}
        px={ROW_PADDING_X}
        h="100%"
        borderBottom="1px solid"
        borderColor="cloud"
      >
        {/* Name, with the enrollment badge alongside it on the top line. The
            badge qualifies the member, not their contact details, so it reads
            with the name — and at the top of the row it's visible while
            scanning names rather than needing a second pass over the small
            print. The name still truncates; the badge never does. */}
        <Box minW={0}>
          <HStack gap={2} align="center" minW={0}>
            <Text
              my={0}
              truncate
              minW={0}
              fontWeight={600}
              fontSize="md"
              color="secondary.500"
            >
              {[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}
            </Text>
            <Box flexShrink={0}>
              <EnrollmentBadge user={user} />
            </Box>
          </HStack>
          <Text my={0} truncate fontSize="xs" color="muted.500">
            {user.email}
          </Text>
        </Box>

        {/* Next Appointment — date/time format and confirmation pill match the
            patient profile / provider Home's appointment rows (StatusPill),
            rather than this column's own "· Unconfirmed" text suffix. */}
        <Box minW={0}>
          {meeting ? (
            <>
              {/* Evergreen rather than the body default: when the visit is
                  the thing being scanned for, its date and time shouldn't
                  sit at the same weight as secondary text. */}
              <HStack gap={2} align="center">
                <Text
                  my={0}
                  flexShrink={0}
                  whiteSpace="nowrap"
                  fontSize="sm"
                  fontWeight={500}
                  color="evergreen"
                >
                  {MEETING_DATE.format(meeting.date)}
                </Text>
                <Text
                  my={0}
                  flexShrink={0}
                  whiteSpace="nowrap"
                  fontSize="sm"
                  fontWeight={500}
                  color="evergreen"
                >
                  {MEETING_TIME.format(meeting.date)}
                </Text>
                <Box flexShrink={0}>
                  <StatusPill
                    size="sm"
                    status={meeting.confirmed ? 'confirmed' : 'unconfirmed'}
                  />
                </Box>
              </HStack>
              {/* Who the visit is with is the second thing scanned after
                  when it is — bold so it separates from the row's other
                  small print. */}
              <Text
                my={0}
                truncate
                fontSize="xs"
                fontWeight={600}
                color="muted.500"
              >
                {meeting.coachFirstName
                  ? `Coach ${meeting.coachFirstName}`
                  : meeting.attendee}
              </Text>
            </>
          ) : (
            <Text my={0} fontSize="sm" color="muted.400">
              None scheduled
            </Text>
          )}
        </Box>

        {/* Last Engaged. Blank rather than a dash for a member who has never
            logged in: they have no engagement date, which is a real state and
            not a data gap, and a dash in every never-logged-in row reads as
            one. Same reasoning the Outcome Goal column used before round 7
            removed it. */}
        <Box minW={0}>
          {engagedOn && (
            <Text
              my={0}
              whiteSpace="nowrap"
              fontSize="sm"
              color="secondary.500"
            >
              {ENGAGED_DATE.format(engagedOn)}
            </Text>
          )}
        </Box>

        <Box color="muted.400" display="flex" justifyContent="flex-end">
          <ChevronRight size={18} />
        </Box>
      </Box>
    </Hoverable>
  );
};
